import { useState, type FormEvent, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase } from "@/lib/supabase";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const SUBJECT_LABELS: Record<string, string> = {
  Engines: "Engine Enquiry",
  Transmissions: "Transmission Enquiry",
  "Body Parts": "Body Parts Enquiry",
  General: "General Enquiry",
};

export default function ContactUs() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { contactDetails, pricing, site, contactDisplay } = useSiteSettings();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  // Prefilled state driven by query params passed from a product "Enquire" button
  const [partName, setPartName] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [prefillImage, setPrefillImage] = useState("");

  useEffect(() => {
    const category = searchParams.get("category") || "";
    const subjectParam = searchParams.get("subject") || "";
    const part = searchParams.get("part") || "";
    const make = searchParams.get("make") || "";
    const model = searchParams.get("model") || "";
    const year = searchParams.get("year") || "";
    const price = searchParams.get("price") || "";
    const desc = searchParams.get("desc") || "";
    const image = searchParams.get("image") || "";

    if (part) setPartName(part);
    if (image) setPrefillImage(image);
    if (subjectParam) setSubject(subjectParam);
    else if (category) setSubject(SUBJECT_LABELS[category] || "General Enquiry");

    const veh = [make, model].filter(Boolean).join(" ");
    const vehWithYear = year && veh ? `${veh} (${year})` : veh || year || "";
    if (vehWithYear) setVehicle(vehWithYear);

    let msg = desc || "";
    if (price && pricing.show_prices) msg += `\n\nListed price: ${price}`;
    if (msg) setMessage(`Hi, I am enquiring about the following part:\n\n${msg}\n\nPlease confirm availability and postage.`);
  }, [searchParams, pricing.show_prices]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (formData.get("company_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }

    const name = (formData.get("name") as string) || "";
    const email = (formData.get("email") as string) || "";
    const phone = (formData.get("phone") as string) || "";
    const subjectValue = (formData.get("subject") as string) || "";
    const part = (formData.get("part") as string) || "";
    const vehicleValue = (formData.get("vehicle") as string) || "";
    const notes = (formData.get("message") as string) || "";

    setStatus("loading");
    setFormError("");

    try {
      // Save the enquiry to the database for the admin dashboard
      const { error: insertError } = await supabase.from("enquiries").insert({
        product_name: part || null,
        vehicle: vehicleValue || null,
        subject: subjectValue || "General Enquiry",
        name: name || null,
        email: email || null,
        phone: phone || null,
        notes: notes || null,
        status: "new",
        image_url: prefillImage || null,
      });
      if (insertError) throw insertError;

      // Fire-and-forget: email notification (admin + customer) via Resend
      supabase.functions
        .invoke("send-enquiry-email", {
          body: {
            name,
            email,
            phone,
            subject: subjectValue,
            product_name: part,
            vehicle: vehicleValue,
            notes,
            image_url: prefillImage || null,
          },
        })
        .catch(() => {});

      setStatus("success");
      form.reset();
      setPartName("");
      setVehicle("");
      setMessage("");
      setSubject("");
      setPrefillImage("");
    } catch (err) {
      setStatus("error");
      setFormError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  };

  const phones = contactDetails.filter((c) => c.type === "phone");
  const emails = contactDetails.filter((c) => c.type === "email");
  const addresses = contactDetails.filter((c) => c.type === "address");
  const mapQuery = addresses[0]?.value
    ? encodeURIComponent(addresses[0].value)
    : "Winnellie%2C%20Darwin%20NT%200820%2C%20Australia";

  return (
    <div className="min-h-screen bg-white font-body">
      <Navbar />
      <main className="w-full bg-neutral-50">
        <section className="w-full bg-brand-dark py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6 text-center">
            <span className="text-xs font-medium uppercase tracking-widest text-brand-gold">
              {t("contact.badge")}
            </span>
            <h1 className="mt-2 font-heading text-3xl md:text-4xl font-semibold uppercase text-white">
              {t("contact.title")}
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-white/60 leading-relaxed">
              {t("contact.desc")}
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {/* Form */}
            <div className="rounded-lg bg-white border border-neutral-200 p-6 md:p-8">
              <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">
                {t("contact.sendEnquiry")}
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                {t("contact.formDesc")}
              </p>

              {partName && (
                <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-information-line mt-0.5"></i>
                  <p>{t("contact.prefill")}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="hp-field">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-dark">{t("contact.name")}</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                      placeholder={t("contact.namePlaceholder")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-dark">{t("contact.email")}</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                      placeholder={t("contact.emailPlaceholder")}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-dark">{t("contact.phone")}</label>
                    <input
                      type="tel"
                      name="phone"
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                      placeholder={t("contact.phonePlaceholder")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-dark">{t("contact.subject")}</label>
                    <select
                      name="subject"
                      required
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                    >
                      <option value="">{t("contact.subjectPlaceholder")}</option>
                      <option value="Engine Enquiry">{t("contact.subjectEngine")}</option>
                      <option value="Transmission Enquiry">{t("contact.subjectTransmission")}</option>
                      <option value="Body Parts Enquiry">{t("contact.subjectBody")}</option>
                      <option value="Special Order / Part Request">{t("contact.subjectSpecialOrder")}</option>
                      <option value="General Enquiry">{t("contact.subjectGeneral")}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-dark">
                    {t("contact.part")}
                  </label>
                  <input
                    type="text"
                    name="part"
                    value={partName}
                    onChange={(e) => setPartName(e.target.value)}
                    className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                    placeholder={t("contact.partPlaceholder")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-dark">
                    {t("contact.vehicle")}
                  </label>
                  <input
                    type="text"
                    name="vehicle"
                    value={vehicle}
                    onChange={(e) => setVehicle(e.target.value)}
                    className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                    placeholder={t("contact.vehiclePlaceholder")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-dark">{t("contact.notes")}</label>
                  <textarea
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={500}
                    rows={4}
                    className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                    placeholder={t("contact.notesPlaceholder")}
                  />
                  <p className="mt-1 text-xs text-neutral-400">{t("contact.maxChars")}</p>
                </div>

                {status === "error" && (
                  <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {formError}
                  </div>
                )}
                {status === "success" && (
                  <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                    {t("contact.success")}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white disabled:opacity-60 whitespace-nowrap"
                >
                  <i className="ri-send-plane-line"></i>
                  {status === "loading" ? t("contact.sending") : t("contact.send")}
                </button>
              </form>
            </div>

            {/* Contact details + map */}
            <div className="flex flex-col gap-6">
              <div className="rounded-lg bg-white border border-neutral-200 p-6 md:p-8">
                <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">
                  {t("contact.details")}
                </h2>
                <ul className="mt-4 space-y-4 text-sm text-neutral-600">
                  {contactDisplay.address_contact && addresses.map((a) => (
                    <li key={a.id} className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-dark text-brand-gold">
                        <i className="ri-map-pin-line"></i>
                      </span>
                      <div>
                        <p className="font-medium text-brand-dark">{t("contact.addressLabel")}</p>
                        <p>{a.value}</p>
                      </div>
                    </li>
                  ))}
                  {contactDisplay.phone_contact && phones.map((p) => (
                    <li key={p.id} className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-dark text-brand-gold">
                        <i className="ri-phone-line"></i>
                      </span>
                      <div>
                        <p className="font-medium text-brand-dark">{t("contact.phoneLabel")}</p>
                        <a href={`tel:${p.value.replace(/\s/g, "")}`} className="hover:text-brand-gold">
                          {p.value}
                        </a>
                      </div>
                    </li>
                  ))}
                  {contactDisplay.email_contact && emails.map((em) => (
                    <li key={em.id} className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-dark text-brand-gold">
                        <i className="ri-mail-line"></i>
                      </span>
                      <div>
                        <p className="font-medium text-brand-dark">{t("contact.emailLabel")}</p>
                        <a href={`mailto:${em.value}`} className="hover:text-brand-gold">
                          {em.value}
                        </a>
                      </div>
                    </li>
                  ))}
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-dark text-brand-gold">
                      <i className="ri-time-line"></i>
                    </span>
                    <div>
                      <p className="font-medium text-brand-dark">{t("contact.hoursLabel")}</p>
                      <p>{t("footer.monFri")}</p>
                      <p>{t("contact.sat")}</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="overflow-hidden rounded-lg border border-neutral-200">
                <iframe
                  title={`${site.site_name} location map`}
                  src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                  className="h-72 w-full"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}